import React, { useState, useEffect } from 'react';
import { apiUrl } from '../config/api';
import BuyUnitsModal from './BuyUnitsModal';

const AI_OPTIONS = [
  { id: 'claude', name: 'Claude', icon: '🧠', color: '#667eea', desc: 'Deep reasoning' },
  { id: 'gpt', name: 'GPT-4o', icon: '✨', color: '#11998e', desc: 'Creative & versatile' },
  { id: 'grok', name: 'Grok', icon: '⚡', color: '#eb3349', desc: 'Fast & witty' },
  { id: 'deepseek', name: 'DeepSeek', icon: '🔮', color: '#f093fb', desc: 'Technical depth' }
];

export default function NewConversationModal({ onClose, onCreated, currentBalance: initialBalance }) {
  const [task, setTask] = useState('');
  const [selectedAIs, setSelectedAIs] = useState(['claude', 'gpt', 'grok', 'deepseek']);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [liveBalance, setLiveBalance] = useState(initialBalance || 0);
  const [showBuyUnits, setShowBuyUnits] = useState(false);

  useEffect(() => {
    fetchBalance();
  }, []);

  const fetchBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(apiUrl('/api/units/balance'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.balance !== undefined) setLiveBalance(data.balance);
    } catch (e) {
      console.error('Failed to fetch balance');
    }
  };

  const toggleAI = (aiId) => {
    setSelectedAIs(prev => {
      if (prev.includes(aiId)) {
        if (prev.length <= 1) return prev;
        return prev.filter(id => id !== aiId);
      }
      return [...prev, aiId];
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!task.trim() || selectedAIs.length < 1) return;
    
    await fetchBalance();
    
    if (liveBalance < selectedAIs.length) {
      return;
    }

    setLoading(true);
    setError('');
    setStatus('Creating conversation...');

    try {
      const formData = new FormData();
      formData.append('initialTask', task);
      formData.append('title', task.slice(0, 100));
      formData.append('selectedAIs', JSON.stringify(selectedAIs));
      files.forEach(file => formData.append('files', file));

      const token = localStorage.getItem('token');
      const response = await fetch(apiUrl('/api/conversations'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const contentType = response.headers.get('content-type') || '';
      
      if (contentType.includes('text/event-stream')) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let conversationData = null;
        let responsesReceived = 0;
        let buffer = '';

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              console.log('Stream ended, responses received:', responsesReceived);
              // Stream ended - if we have conversation data and responses, redirect
              if (conversationData && responsesReceived > 0) {
                onCreated(conversationData);
              } else if (conversationData) {
                // Conversation created but no responses - still redirect, page will show empty
                setStatus('Completed - loading conversation...');
                setTimeout(() => onCreated(conversationData), 500);
              } else {
                setError('Connection closed unexpectedly');
                setLoading(false);
              }
              break;
            }

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6));
                  console.log('SSE Event:', data.type, data);
                  
                  if (data.type === 'conversation') {
                    conversationData = data.conversation;
                    setStatus(`Starting with ${data.activeAIs?.length || selectedAIs.length} AIs...`);
                    if (data.unitsBalance !== undefined) setLiveBalance(data.unitsBalance);
                  } else if (data.type === 'thinking') {
                    const aiName = AI_OPTIONS.find(a => a.id === data.ai)?.name || data.ai;
                    setStatus(`${aiName} thinking... (${data.position}/${data.total})`);
                  } else if (data.type === 'response') {
                    responsesReceived++;
                    const aiName = AI_OPTIONS.find(a => a.id === data.ai)?.name || data.ai;
                    setStatus(`${aiName} responded! (${responsesReceived}/${selectedAIs.length})`);
                    if (data.unitsBalance !== undefined) {
                      setLiveBalance(data.unitsBalance);
                      window.dispatchEvent(new CustomEvent('unitUpdate', { detail: { unitsBalance: data.unitsBalance } }));
                    }
                  } else if (data.type === 'complete') {
                    console.log('Complete event received, responses:', responsesReceived);
                    if (conversationData) {
                      setStatus('Complete! Loading conversation...');
                      onCreated(conversationData);
                    }
                  } else if (data.type === 'error') {
                    setError(data.message || 'An error occurred');
                    setLoading(false);
                  }
                } catch (parseErr) {
                  console.error('Parse error:', parseErr, 'Line:', line);
                }
              }
            }
          }
        } catch (streamErr) {
          console.error('Stream error:', streamErr);
          if (conversationData) {
            onCreated(conversationData);
          } else {
            setError('Connection error: ' + streamErr.message);
            setLoading(false);
          }
        }
      } else {
        const data = await response.json();
        if (response.status === 402) {
          setLiveBalance(data.currentBalance || 0);
          setLoading(false);
          return;
        }
        if (response.ok && data.conversation) {
          onCreated(data.conversation);
        } else {
          setError(data.error || data.message || 'Failed');
          setLoading(false);
        }
      }
    } catch (err) {
      console.error('Request error:', err);
      setError('Network error: ' + err.message);
      setLoading(false);
    }
  };

  const unitCost = selectedAIs.length;
  const needsMoreUnits = liveBalance < unitCost;

  if (showBuyUnits) {
    return (
      <BuyUnitsModal 
        onClose={() => {
          setShowBuyUnits(false);
          fetchBalance();
        }}
        currentBalance={liveBalance}
      />
    );
  }

  return (
    <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
    }}>
      <div style={{
        width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto',
        background: 'linear-gradient(135deg, #0d1525, #0a0f1c)', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '24px', padding: '28px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h2 style={{ fontSize: '22px', fontWeight: '600', color: 'white', marginBottom: '4px' }}>✨ New Conversation</h2>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>Collaborate with your AI team</p>
          </div>
          <button onClick={onClose} disabled={loading} style={{ background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: '10px', width: '36px', height: '36px', color: 'rgba(255,255,255,0.6)', fontSize: '20px', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}>×</button>
        </div>

        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '12px', padding: '12px 16px', marginBottom: '20px', color: '#f87171', fontSize: '14px' }}>{error}</div>
        )}

        {needsMoreUnits && !loading && (
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(245, 158, 11, 0.15))',
            border: '1px solid rgba(251, 191, 36, 0.4)', 
            borderRadius: '16px', 
            padding: '20px', 
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🪙</div>
            <h3 style={{ color: '#fbbf24', fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Need More Units
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginBottom: '16px' }}>
              You have <strong>{liveBalance}</strong> unit{liveBalance !== 1 ? 's' : ''} but need <strong>{unitCost}</strong> for this conversation.
              {liveBalance > 0 && <><br/>Try selecting fewer AIs, or get more units to continue.</>}
            </p>
            <button
              onClick={() => setShowBuyUnits(true)}
              style={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                border: 'none',
                borderRadius: '12px',
                padding: '14px 32px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              🛒 Get More Units
            </button>
          </div>
        )}

        {/* Unit Cost Preview */}
        <div style={{ 
          background: needsMoreUnits ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)', 
          border: `1px solid ${needsMoreUnits ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}`, 
          borderRadius: '12px', 
          padding: '12px 16px', 
          marginBottom: '20px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🪙</span>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>This round will cost</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: needsMoreUnits ? '#ef4444' : '#22c55e', fontWeight: '700', fontSize: '18px' }}>{unitCost} units</span>
            <span style={{ color: needsMoreUnits ? '#fca5a5' : 'rgba(255,255,255,0.4)', fontSize: '12px' }}>({liveBalance} available)</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: '500', marginBottom: '10px' }}>What should your AI team work on?</label>
            <textarea
              value={task}
              onChange={(e) => setTask(e.target.value)}
              placeholder="e.g., Help me brainstorm marketing strategies..."
              rows={4}
              disabled={loading}
              style={{ width: '100%', padding: '16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', color: 'white', fontSize: '15px', resize: 'vertical', outline: 'none', boxSizing: 'border-box', opacity: loading ? 0.6 : 1 }}
            />
          </div>

          <div style={{ marginBottom: '24px', opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
            <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: '500', marginBottom: '12px' }}>Select AIs ({selectedAIs.length} selected = {selectedAIs.length} units)</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              {AI_OPTIONS.map(ai => {
                const isSelected = selectedAIs.includes(ai.id);
                return (
                  <button key={ai.id} type="button" onClick={() => toggleAI(ai.id)} style={{
                    padding: '16px', background: isSelected ? `${ai.color}20` : 'rgba(255,255,255,0.03)',
                    border: `2px solid ${isSelected ? ai.color : 'rgba(255,255,255,0.08)'}`,
                    borderRadius: '14px', cursor: 'pointer', textAlign: 'left'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '22px' }}>{ai.icon}</span>
                      <span style={{ color: 'white', fontWeight: '600', fontSize: '15px' }}>{ai.name}</span>
                      {isSelected && <span style={{ marginLeft: 'auto', color: ai.color }}>✓</span>}
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>{ai.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom: '24px', opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
            <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: '500', marginBottom: '10px' }}>Attach files (optional) - Text files only</label>
            <div style={{ border: '2px dashed rgba(255,255,255,0.1)', borderRadius: '14px', padding: '24px', textAlign: 'center', cursor: 'pointer' }}
              onClick={() => document.getElementById('file-input').click()}>
              <input id="file-input" type="file" multiple accept="text/plain,.txt,.md,.pdf,.docx,.json,.csv" onChange={(e) => setFiles(Array.from(e.target.files))} style={{ display: 'none' }} />
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>📁</div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>Drop files or <span style={{ color: '#667eea' }}>browse</span></div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginTop: '4px' }}>.txt, .md, .pdf, .docx, .json, .csv</div>
            </div>
            {files.length > 0 && (
              <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {files.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', fontSize: '13px' }}>
                    <span>📄</span><span style={{ color: 'rgba(255,255,255,0.8)' }}>{f.name}</span>
                    <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={onClose} disabled={loading} style={{ flex: 1, padding: '14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: 'rgba(255,255,255,0.7)', fontSize: '15px', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}>Cancel</button>
            <button type="submit" disabled={loading || !task.trim() || needsMoreUnits} style={{
              flex: 2, padding: '14px',
              background: loading ? 'linear-gradient(135deg, #667eea, #764ba2)' : (!needsMoreUnits && task.trim()) ? 'linear-gradient(135deg, #667eea, #764ba2)' : 'rgba(255,255,255,0.1)',
              border: 'none', borderRadius: '12px',
              color: loading ? 'white' : (!needsMoreUnits && task.trim()) ? 'white' : 'rgba(255,255,255,0.4)',
              fontSize: '15px', fontWeight: '600', cursor: (loading || !task.trim() || needsMoreUnits) ? 'not-allowed' : 'pointer'
            }}>
              {loading ? `⏳ ${status}` : needsMoreUnits ? '🔒 Need More Units' : `🚀 Start (${unitCost} units)`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
